import Im, { fromJS } from "immutable";
import { Endpoint } from "models/apiDocumentation/apiDocumentationModels";
import React, { ReactElement } from "react";
import "swagger-ui-react/swagger-ui.css";
import {
  GetComponentType as ComponentFetcher,
  MarkdownProps,
  OperationContainerProps,
  Plugin,
} from "types/apiDocs";

import { CustomSwaggerContainer, Surtitle, Title } from "./style";

interface PluginProps {
  endpointData: Endpoint;
}

interface ComponentProps {
  getComponent: ComponentFetcher;
}

interface LayoutProps extends PluginProps, ComponentProps {}

const SingleEndpointLayout: React.FC<LayoutProps> = ({
  endpointData,
  getComponent,
}) => {
  const path = endpointData.url;
  const method = "get";

  const op = fromJS({ path, method });

  const SwaggerSvgAssets = getComponent("SvgAssets");
  const Markdown: React.FC<MarkdownProps> = getComponent("Markdown");

  // This fetches the component defined here: https://github.com/swagger-api/swagger-ui/blob/master/src/core/containers/OperationContainer.jsx
  // which currently uses the deprecated method `UNSAFE_componentWillReceiveProps`.
  // This will result in console warnings on this page until this is fixed in swagger-ui.
  // See open issue: https://github.com/swagger-api/swagger-ui/issues/5729
  const OperationContainer: React.FC<OperationContainerProps> = getComponent(
    "OperationContainer",
    true
  );

  return (
    <>
      <Surtitle>{endpointData.tags[0]}</Surtitle>
      {/*
      Only the first tag is used for categorisation purposes.
      Other tags are used for discoverability. See wiki page for more details:
      https://elxnfa.visualstudio.com/Insights%20Solution/_wiki/wikis/Insights-Solution.wiki/7817/API?anchor=tagging
      */}
      <Title>{endpointData.summary}</Title>
      <Markdown source={endpointData.description} />
      <CustomSwaggerContainer className="swagger-ui">
        <SwaggerSvgAssets />
        <OperationContainer
          specPath={Im.List(["paths", path, method])}
          op={op}
          path={path}
          method={method}
        />
      </CustomSwaggerContainer>
    </>
  );
};

export const EndpointPlugin = ({ endpointData }: PluginProps): Plugin => ({
  components: {
    [endpointData.url]: ({ getComponent }: ComponentProps): ReactElement => (
      <SingleEndpointLayout
        endpointData={endpointData}
        getComponent={getComponent}
      />
    ),
  },
});

export default EndpointPlugin;
